import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const generateUniqueID = () => {
  return "_" + Math.random().toString(36).substr(2, 9);
};

const citizenProfileService = {
  getCitizenProfile: async (collectionName, docId) => {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        console.error("No such document!");
        return null;
      }
    } catch (error) {
      console.error("Error reading document: ", error);
      throw error;
    }
  },

  updateCitizenProfile: async (collectionName, docId, data) => {
    try {
      const docRef = doc(db, collectionName, docId);
      const updatedData = { ...data, updated_at: new Date() };
      console.log("Updated dataaa:", updatedData);
      await updateDoc(docRef, updatedData);
      console.log("Document successfully updated in here!");
      return { id: docId, ...updatedData };
    } catch (error) {
      console.error("Error updating document: ", error);
      throw error;
    }
  },

  getHouseholdInfo: async (householdId) => {
    try {
      // Validate householdId
      if (!householdId || typeof householdId !== "string") {
        console.log("Invalid or missing household ID");
        return {
          annual_income: "",
          owner_or_renter: "",
          insurance: false,
          household_size: "",
          home_insurance: false,
          home_insurance_type: "",
          automotive_insurance: false,
          automotive_insurance_type: "",
          additional_insurance_type: "",
          updated_at: null,
        };
      }

      const docRef = doc(
        db,
        "household",
        householdId,
        "household_info",
        "details"
      );
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          annual_income: data.annual_income || "",
          owner_or_renter: data.owner_or_renter || "",
          insurance: Boolean(data.insurance),
          household_size: data.household_size || "",
          disability: Boolean(data.disability),
          military_vet: Boolean(data.military_vet),
          home_insurance: Boolean(data.home_insurance),
          home_insurance_type: data.home_insurance_type || "",
          automotive_insurance: Boolean(data.automotive_insurance),
          automotive_insurance_type: data.automotive_insurance_type || "",
          additional_insurance_type: data.additional_insurance_type || "",
          updated_at: data.updated_at ? data.updated_at.toDate() : null,
        };
      } else {
        console.log("No household info document found");
        return {
          annual_income: "",
          owner_or_renter: "",
          insurance: false,
          household_size: "",
          disability: false,
          military_vet: false,
          home_insurance: false,
          home_insurance_type: "",
          automotive_insurance: false,
          automotive_insurance_type: "",
          additional_insurance_type: "",
          updated_at: null,
        };
      }
    } catch (error) {
      console.error("Error getting household info: ", error);
      throw error;
    }
  },

  updateHouseholdInfo: async (householdId, data) => {
    try {
      if (!householdId || typeof householdId !== "string") {
        throw new Error("Valid household ID string is required");
      }

      // Log the incoming data for debugging
      console.log("Incoming data:", data);

      // Define allowed fields and their default values
      const allowedFields = {
        annual_income: "",
        owner_or_renter: "",
        insurance: false,
        household_size: "",
        home_insurance: false,
        home_insurance_type: "",
        automotive_insurance: false,
        automotive_insurance_type: "",
        additional_insurance_type: "",
        updated_at: new Date(),
      };

      // Ensure data is an object
      const inputData = data || {};

      // Clean the data by only including allowed fields
      const cleanData = Object.keys(allowedFields).reduce((acc, key) => {
        // Handle each field type appropriately
        if (key === "updated_at") {
          acc[key] =
            inputData[key] instanceof Date
              ? Timestamp.fromDate(inputData[key])
              : Timestamp.fromDate(new Date());
        } else if (typeof allowedFields[key] === "boolean") {
          acc[key] = Boolean(inputData[key]);
        } else {
          acc[key] = inputData[key]?.toString() || allowedFields[key];
        }
        return acc;
      }, {});

      // Log the cleaned data for debugging
      console.log("Cleaned data to save:", cleanData);

      const docRef = doc(
        db,
        "household",
        householdId,
        "household_info",
        "details"
      );

      // Always use setDoc without merge option to ensure clean replacement
      await setDoc(docRef, cleanData);

      return { id: "details", ...cleanData };
    } catch (error) {
      console.error("Error updating/creating household info document: ", error);
      throw error;
    }
  },

  // Get all household members for a citizen
  getHouseholdMembers: async (householdId, householdMemberId) => {
    try {
      // Validate householdId
      if (!householdId || typeof householdId !== "string") {
        console.log("Invalid or missing household ID");
        return []; // Return empty array if no valid householdId
      }

      const householdMembersRef = collection(
        db,
        "household",
        householdId,
        "household_members"
      );
      const snapshot = await getDocs(householdMembersRef);

      // Map the documents to match the grid structure
      return snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            household_member_id: data.household_member_id || doc.id,
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            date_of_birth: data.date_of_birth || "",
            relationship: data.relationship || "",
            disability: data.disability || false,
            military_vet: data.military_vet || false,
            is_deleted: data.is_deleted || false,
            email: data.email || "",
            acc_created: data.acc_created || "",
            type: data.type || "",
            associatedDisasters: data.associatedDisasters || [],
            gender: data.gender || "",
            ethnicity: data.ethnicity || "",
            fema_number: data.fema_number || "",
          };
        })
        .filter(
          (member) =>
            !member.is_deleted &&
            member.household_member_id !== householdMemberId
        ); // Filter out deleted members and current member
    } catch (error) {
      console.error("Error getting household members: ", error);
      return []; // Return empty array on error
    }
  },

  // Add a new household member
  addHouseholdMember: async (householdId, memberData) => {
    try {
      console.log("Adding household member:", memberData);

      // Check if member with this email already exists
      const householdMembersRef = collection(
        db,
        "household",
        householdId,
        "household_members"
      );
      const snapshot = await getDocs(householdMembersRef);
      let existingMember = null;

      if (memberData.email) {
        // If email is provided, only check for email match
        existingMember = snapshot.docs.find(
          (doc) => doc.data().email === memberData.email
        );
      } else {
        // If no email provided, check for matching name and DOB
        existingMember = snapshot.docs.find((doc) => {
          const data = doc.data();
          return (
            data.first_name?.toLowerCase() ===
              memberData.first_name?.toLowerCase() &&
            data.last_name?.toLowerCase() ===
              memberData.last_name?.toLowerCase() &&
            data.date_of_birth === memberData.date_of_birth
          );
        });
      }
      if (existingMember) {
        // Update existing member's is_deleted to false
        const memberRef = doc(
          db,
          "household",
          householdId,
          "household_members",
          existingMember.id
        );
        await updateDoc(memberRef, {
          is_deleted: false,
          household_member_id: memberData.household_member_id,
          acc_created: memberData.acc_created,
          relationship: memberData.relationship,
          disability: memberData.disability,
          military_vet: memberData.military_vet,
          email: memberData.email,
          first_name: memberData.first_name,
          last_name: memberData.last_name,
          date_of_birth: memberData.date_of_birth,
          gender: memberData.gender,
          ethnicity: memberData.ethnicity,
          fema_number: memberData.fema_number,
        });
        return { ...existingMember.data(), id: existingMember.id };
      } else {
        // Add new member
        const docRef = await addDoc(householdMembersRef, memberData);
        console.log("Document written with ID: ", docRef.id);
        return { ...memberData, id: docRef.id };
      }
    } catch (error) {
      console.error("Error adding household member: ", error);
      throw error;
    }
  },

  // Update a household member
  updateHouseholdMember: async (householdId, householdMemberID, data) => {
    try {
      const memberRef = doc(
        db,
        "household",
        householdId,
        "household_members",
        householdMemberID
      );
      await setDoc(memberRef, data, { merge: true });
      console.log("Household member updated successfully");
      return { id: householdMemberID, ...data };
    } catch (error) {
      console.error("Error updating household member: ", error);
      throw error;
    }
  },

  // Get all pets for a citizen
  getPets: async (householdId) => {
    try {
      // Validate householdId
      if (!householdId || typeof householdId !== "string") {
        console.log("Invalid or missing household ID");
        return []; // Return empty array if no valid householdId
      }

      const petsRef = collection(db, "household", householdId, "pets");
      const snapshot = await getDocs(petsRef);

      // Map the documents to match the pets grid structure
      return snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            pet_id: data.pet_id || doc.id,
            pet_name: data.pet_name || "",
            pet_color: data.pet_color || "",
            pet_breed: data.pet_breed || "",
            pet_size: data.pet_size || "",
            is_deleted: data.is_deleted || false,
            created_at: data.created_at || null,
            updated_at: data.updated_at || null,
            pet_coat: data.pet_coat || "",
            pet_image: data.pet_image || [],
            associatedDisasters: data.associatedDisasters || [],
          };
        })
        .filter((pet) => !pet.is_deleted); // Filter out deleted pets
    } catch (error) {
      console.error("Error getting pets: ", error);
      return []; // Return empty array on error instead of throwing
    }
  },

  // Add a new pet
  addPet: async (householdId, petData) => {
    console.log("Adding pet:", petData);
    console.log("Household ID:", householdId);
    try {
      const petID = generateUniqueID();
      const docRef = await addDoc(
        collection(db, "household", householdId, "pets"),
        {
          ...petData,
          created_at: new Date(),
          pet_id: petID, // Generate a unique ID for the new pet
        }
      );
      console.log("Document written with ID: ", docRef.id);
      return { ...petData, id: docRef.id, petId: docRef.id };
    } catch (error) {
      console.error("Error adding pet: ", error);
      throw error;
    }
  },

  // Update a pet
  updatePet: async (householdId, petId, petData) => {
    try {
      const petRef = doc(db, "household", householdId, "pets", petId);
      await setDoc(
        petRef,
        { ...petData, updated_at: new Date() },
        { merge: true }
      );
      console.log("Pet updated successfully");
      return { id: petId, ...petData };
    } catch (error) {
      console.error("Error updating pet: ", error);
      throw error;
    }
  },

  // Delete a pet
  deletePet: async (householdId, petId) => {
    try {
      console.log("Deleting pet:", householdId, petId);
      const petRef = doc(db, "household", householdId, "pets", petId);
      await deleteDoc(petRef);
      console.log("Pet deleted successfully");
    } catch (error) {
      console.error("Error deleting pet: ", error);
      throw error;
    }
  },

  toggleDisplaced: async (householdId, toggle) => {
    const userRef = doc(db, "users", householdId);
    try {
      console.log("Toggling displaced checkbox", householdId, toggle);
      await updateDoc(userRef, {
        is_displaced: toggle,
      });
      console.log("Displaced checkbox toggled!");
    } catch (error) {
      console.log("Error toggling displaced checkbox: ", error);
      throw error;
    }
  },

  updateDisplacedDetails: async (householdId, data) => {
    const userRef = doc(db, "users", householdId);
    try {
      console.log("Updating displaced details...", householdId, data);
      await updateDoc(userRef, {
        ...data,
        d_updated_at: new Date(),
      });
      console.log("Displaced details updated!");
    } catch (error) {
      console.log("Error updating displaced details: ", error);
      throw error;
    }
  },

  toggleOwnOtherProperties: async (householdId, toggle) => {
    const userRef = doc(db, "users", householdId);
    try {
      console.log(
        "Toggling Own Other Properties checkbox",
        householdId,
        toggle
      );
      await updateDoc(userRef, {
        own_other_properties: toggle,
      });
      console.log("Own Other Properties checkbox toggled!");
    } catch (error) {
      console.log("Error toggling Own Other Properties checkbox: ", error);
      throw error;
    }
  },

  ownOtherPropertiesFollowup: async (householdId, followup) => {
    const userRef = doc(db, "users", householdId);
    try {
      console.log("Own Other Properties follow up", householdId, followup);
      await updateDoc(userRef, {
        own_other_properties_followup: followup,
      });
      console.log("Own Other Properties follow up!");
    } catch (error) {
      console.log("Error Own Other Properties follow up: ", error);
      throw error;
    }
  },
};
export default citizenProfileService;
